import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, X, Sparkles, Search, RefreshCw, ArrowUpRight, BookOpen, Loader2,
  Send, Archive, Undo2, Pencil, XCircle, CheckCircle2,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_META = {
  published: { label: "Publicado", cls: "bg-emerald-500/20 text-emerald-200 border-emerald-500/30" },
  draft: { label: "Borrador", cls: "bg-slate-500/20 text-slate-200 border-slate-500/30" },
  pending_review: { label: "En revisión", cls: "bg-amber-500/20 text-amber-200 border-amber-500/30" },
  rejected: { label: "Rechazado", cls: "bg-red-500/20 text-red-200 border-red-500/30" },
  archived: { label: "Archivado", cls: "bg-gray-500/20 text-gray-300 border-gray-500/30" },
};
const TABS = [
  { id: "all", label: "Todos" },
  { id: "published", label: "Publicados" },
  { id: "pending_review", label: "En revisión" },
  { id: "draft", label: "Borradores" },
  { id: "archived", label: "Archivados" },
  { id: "rejected", label: "Rechazados" },
];

export default function AdminCatalogo() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const canActOnPrograms = superadmin || !!user?.roles?.includes("staff_dcc");
  const [areas, setAreas] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({});
  const [busySlug, setBusySlug] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { program, targetStatus }
  const [rejectionReason, setRejectionReason] = useState("");

  const refreshCounts = useCallback(async () => {
    const results = await Promise.all(TABS.map(async (t) => {
      try {
        const { data } = await api.get("/admin/programs", { params: { status: t.id, limit: 1 } });
        return [t.id, data.total || 0];
      } catch { return [t.id, 0]; }
    }));
    setCounts(Object.fromEntries(results));
  }, []);

  useEffect(() => {
    api.get("/catalog/areas").then((r) => setAreas(r.data));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status, limit: 200 };
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/programs", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [status, q]);

  useEffect(() => { load(); }, [load]);

  // load counts per tab (background, non-blocking)
  useEffect(() => { refreshCounts(); }, [refreshCounts, items]);

  // Optimistic status change. `program` is the row object; `targetStatus`
  // is one of `published|draft|pending_review|archived|rejected`.
  const changeStatus = async (program, targetStatus, reason) => {
    const prevItems = items;
    const prevBadge = program.status_badge;
    setBusySlug(program.slug);
    // Optimistic: if the current filter is a specific tab, drop the row when
    // new status no longer matches. Otherwise just update badge in-place.
    const nextItems = (() => {
      if (status === "all" || status === targetStatus) {
        return items.map((it) => it.slug === program.slug
          ? { ...it, status_badge: targetStatus, is_published: targetStatus === "published",
              review_status: targetStatus === "published" || targetStatus === "archived"
                ? null : targetStatus }
          : it);
      }
      return items.filter((it) => it.slug !== program.slug);
    })();
    setItems(nextItems);
    try {
      const body = { status: targetStatus };
      if (reason) body.reason = reason;
      const { data } = await api.patch(`/admin/programs/${program.slug}/status`, body);
      // Reconcile with server truth
      setItems((cur) => cur.map((it) => it.slug === program.slug
        ? { ...it, status_badge: data.status_badge, is_published: data.is_published,
            review_status: data.review_status, rejection_reason: data.rejection_reason }
        : it));
      const msg = {
        published: "Programa publicado ✓",
        draft: "Movido a borradores",
        pending_review: "Enviado a revisión",
        archived: "Programa archivado",
        rejected: "Programa rechazado",
      }[targetStatus] || "Estado actualizado";
      toast.success(msg);
      refreshCounts();
    } catch (er) {
      // Revert on failure
      setItems(prevItems);
      toast.error(formatApiError(er.response?.data?.detail) || `No se pudo cambiar a ${targetStatus}`);
    } finally {
      setBusySlug(null);
    }
  };

  const requestConfirm = (program, targetStatus) => {
    setRejectionReason("");
    setConfirmAction({ program, targetStatus });
  };
  const runConfirmed = async () => {
    if (!confirmAction) return;
    const { program, targetStatus } = confirmAction;
    const reason = targetStatus === "rejected" ? rejectionReason.trim() : undefined;
    if (targetStatus === "rejected" && !reason) {
      toast.error("Escribe un motivo para rechazar.");
      return;
    }
    setConfirmAction(null);
    await changeStatus(program, targetStatus, reason);
  };

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#93C5FD] font-semibold">Catálogo CMS · vista editorial</p>
          <h1 className="mt-1 text-3xl font-bold text-white font-display">Gestión de programas</h1>
          <p className="mt-1 text-sm text-gray-400">
            Todos los programas del catálogo, sin importar su estado. Filtra por publicación y busca por título, código o slug.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/app/staff/content-studio" data-testid="btn-content-studio"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#111827] hover:bg-white/10 border border-[#F26A21]/40 text-[#F26A21] font-semibold text-sm min-h-[44px]">
            <Sparkles className="w-4 h-4" /> Content Studio
          </Link>
          <Link to="/app/staff/catalogo/generar-con-ia" data-testid="btn-ai-course-builder"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm min-h-[44px]">
            <Sparkles className="w-4 h-4" /> Generar con IA
          </Link>
          <button onClick={() => setShowNew(true)} data-testid="btn-new-program"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-[#B84A0C] hover:bg-primary text-white text-sm font-semibold min-h-[44px]">
            <Plus className="w-4 h-4" /> Nuevo programa
          </button>
        </div>
      </div>

      {/* Tabs + search */}
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <div className="inline-flex gap-1 rounded-lg bg-white/5 border border-white/10 p-1" data-testid="catalog-tabs">
          {TABS.map((t) => {
            const c = counts[t.id];
            const active = status === t.id;
            return (
              <button key={t.id} onClick={() => setStatus(t.id)}
                data-testid={`tab-${t.id}`}
                className={`text-xs px-3 py-1.5 rounded-md font-semibold transition ${active ? "bg-[#F26A21] text-[#0B0F14]" : "text-gray-300 hover:bg-white/5"}`}>
                {t.label}{typeof c === "number" ? <span className="ml-1 opacity-70">({c})</span> : null}
              </button>
            );
          })}
        </div>
        <div className="relative flex-1 min-w-[240px] max-w-[420px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="catalog-search-input"
            placeholder="Buscar por título, código o slug… (ej. HPCM)"
            className="w-full rounded-md bg-white/5 border border-white/10 pl-9 pr-3 py-2 text-sm text-white placeholder:text-gray-500" />
        </div>
        <button onClick={load} data-testid="catalog-refresh-btn"
          className="text-xs text-gray-300 hover:text-white inline-flex items-center gap-1 px-2 py-1">
          <RefreshCw className="w-3 h-3" /> Refrescar
        </button>
        <p className="ml-auto text-xs text-gray-500">
          <span className="text-white font-semibold">{total}</span> {total === 1 ? "programa" : "programas"} en filtro actual
        </p>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-sm text-gray-400 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando programas…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/5 p-8 text-center text-sm text-gray-400" data-testid="catalog-empty">
          Sin resultados para "{q}" en <b>{TABS.find((t) => t.id === status)?.label}</b>.
        </div>
      ) : (
        <div className="grid gap-2" data-testid="catalog-list">
          {items.map((p) => (
            <div key={p.id}
              data-testid={`catalog-row-${p.code}`}
              className="rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] p-3 flex items-center gap-3 flex-wrap group">
              <BookOpen className="w-4 h-4 text-[#F26A21] flex-shrink-0" />
              <div className="flex-1 min-w-[220px]">
                <p className="text-sm font-semibold text-white truncate">{p.title}</p>
                <p className="mt-0.5 text-[11px] text-gray-500 truncate">
                  {p.code} · {p.area_name || "—"} · {p.format} · {p.modality}
                </p>
              </div>
              <div className="text-[10px] text-gray-400">{p.modules_count} módulos</div>
              {p.iada_aval_available && (
                <span className="text-[9px] uppercase tracking-widest bg-[#F26A21]/20 text-[#F26A21] px-1.5 py-0.5 rounded" title="IADA aval disponible">IADA</span>
              )}
              <span
                className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 rounded font-semibold ${STATUS_META[p.status_badge]?.cls || "border-white/10 text-gray-300"} ${p.status_badge === "rejected" && p.rejection_reason ? "cursor-help" : ""}`}
                data-testid={`badge-status-${p.code}`}
                title={p.status_badge === "rejected" && p.rejection_reason ? `Motivo: ${p.rejection_reason}` : undefined}>
                {STATUS_META[p.status_badge]?.label || p.status_badge}
              </span>

              {/* Acciones inline */}
              <div className="flex items-center gap-1 flex-wrap" data-testid={`row-actions-${p.code}`}>
                <RowAction show={canActOnPrograms && ["draft", "pending_review"].includes(p.status_badge)}
                  onClick={() => changeStatus(p, "published")}
                  busy={busySlug === p.slug} testid={`action-publish-${p.code}`}
                  color="emerald" icon={<CheckCircle2 className="w-3 h-3" />} label="Publicar" />

                <RowAction show={canActOnPrograms && (p.status_badge === "draft" || p.status_badge === "rejected")}
                  onClick={() => changeStatus(p, "pending_review")}
                  busy={busySlug === p.slug} testid={`action-review-${p.code}`}
                  color="amber" icon={<Send className="w-3 h-3" />} label="A revisión" />

                <RowAction show={canActOnPrograms && p.status_badge === "pending_review"}
                  onClick={() => requestConfirm(p, "rejected")}
                  busy={busySlug === p.slug} testid={`action-reject-${p.code}`}
                  color="red" icon={<XCircle className="w-3 h-3" />} label="Rechazar" />

                <RowAction show={canActOnPrograms && ["published", "pending_review", "draft", "rejected"].includes(p.status_badge)}
                  onClick={() => requestConfirm(p, "archived")}
                  busy={busySlug === p.slug} testid={`action-archive-${p.code}`}
                  color="gray" icon={<Archive className="w-3 h-3" />} label="Archivar" />

                <RowAction show={canActOnPrograms && p.status_badge === "archived"}
                  onClick={() => changeStatus(p, "draft")}
                  busy={busySlug === p.slug} testid={`action-restore-${p.code}`}
                  color="slate" icon={<Undo2 className="w-3 h-3" />} label="Restaurar" />

                <RowAction show={canActOnPrograms}
                  disabled title="Próximamente"
                  testid={`action-edit-${p.code}`}
                  color="slate" icon={<Pencil className="w-3 h-3" />} label="Editar" />

                <Link to={`/catalogo/${p.slug}`} target="_blank" rel="noreferrer"
                  data-testid={`action-view-${p.code}`}
                  className="text-[10px] font-semibold rounded px-2 py-1 border border-white/15 bg-white/5 text-gray-200 hover:text-white hover:bg-white/10 inline-flex items-center gap-1">
                  <ArrowUpRight className="w-3 h-3" /> Ver
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmación destructiva (archivar / rechazar) */}
      <AlertDialog open={!!confirmAction} onOpenChange={(o) => { if (!o) setConfirmAction(null); }}>
        <AlertDialogContent className="bg-[#111827] border border-white/10 text-white" data-testid="confirm-status-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.targetStatus === "rejected" ? "Rechazar programa" : "Archivar programa"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {confirmAction?.targetStatus === "rejected"
                ? <>El programa <b className="text-white">{confirmAction?.program?.title}</b> pasará a estado <b className="text-red-300">rechazado</b>. Escribe el motivo (obligatorio):</>
                : <>El programa <b className="text-white">{confirmAction?.program?.title}</b> se archivará y dejará de mostrarse en el catálogo público.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirmAction?.targetStatus === "rejected" && (
            <textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}
              data-testid="rejection-reason-input"
              rows={3} maxLength={1000}
              placeholder="Ej. Requiere más profundidad en los ejemplos regulatorios mexicanos…"
              className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white outline-none focus:border-[#F26A21]" />
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel-btn"
              className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={runConfirmed} data-testid="confirm-run-btn"
              className={confirmAction?.targetStatus === "rejected"
                ? "bg-red-500 text-white hover:bg-red-600"
                : "bg-[#F26A21] text-[#0B0F14] hover:brightness-110"}>
              {confirmAction?.targetStatus === "rejected" ? "Rechazar" : "Archivar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showNew && <NewProgramFlow areas={areas} onClose={() => { setShowNew(false); load(); }} />}
    </StaffLayout>
  );
}


function RowAction({ show, disabled, busy, onClick, testid, color, icon, label, title }) {
  if (!show) return null;
  const colors = {
    emerald: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30 hover:bg-emerald-500/25",
    amber: "bg-amber-500/15 text-amber-200 border-amber-500/30 hover:bg-amber-500/25",
    red: "bg-red-500/15 text-red-200 border-red-500/30 hover:bg-red-500/25",
    gray: "bg-white/5 text-gray-200 border-white/15 hover:bg-white/10",
    slate: "bg-white/5 text-gray-300 border-white/15 hover:bg-white/10",
  }[color] || "bg-white/5 text-gray-200 border-white/15 hover:bg-white/10";
  const isDisabled = disabled || busy;
  return (
    <button type="button" onClick={onClick} disabled={isDisabled}
      data-testid={testid} title={title}
      className={`text-[10px] font-semibold border rounded px-2 py-1 inline-flex items-center gap-1 transition ${colors} ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : icon}
      {label}
    </button>
  );
}


function NewProgramFlow({ areas, onClose }) {
  const [step, setStep] = useState(1);
  const [programId, setProgramId] = useState(null);
  const [moduleIds, setModuleIds] = useState([]);
  const [prog, setProg] = useState({
    title: "", code: "", area_id: "", format: "curso",
    duration_text: "20 horas", modality: "online",
    target_audience: "Ejecutivos financieros",
    description: "Programa impartido por DCC.",
    syllabus_summary: "Módulos incluidos en este programa.",
  });

  const createProgram = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/programs", prog);
      setProgramId(data.id);
      setStep(2);
      toast.success("Programa creado");
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  const addModule = async (title, order) => {
    const { data } = await api.post(`/admin/programs/${programId}/modules`, {
      title, order, description: "", estimated_hours: 4,
    });
    setModuleIds([...moduleIds, data.id]);
    return data.id;
  };

  const addLesson = async (moduleId, lesson) => {
    await api.post(`/admin/modules/${moduleId}/lessons`, lesson);
  };

  const finishFlow = async () => {
    // Add 2 modules + 3 lessons (spec acceptance #10)
    try {
      const m1 = await addModule("Módulo 1 · Fundamentos", 1);
      const m2 = await addModule("Módulo 2 · Aplicación práctica", 2);
      await addLesson(m1, { title: "Introducción", order: 1, type: "text",
        payload: { markdown: "# Bienvenida\n\nContenido introductorio.", reading_time_min: 5 }, duration_min: 5 });
      await addLesson(m1, { title: "Video overview", order: 2, type: "video",
        payload: { video_url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4", duration_min: 10 }, duration_min: 10 });
      await addLesson(m2, { title: "Quiz de cierre", order: 1, type: "quiz",
        payload: { passing_score: 70, questions: [
          { id: "q1", question: "¿Qué es DCC?", options: ["Un banco", "Una escuela", "Un fondo", "Un PAC"], correct_index: 1, explanation: "DCC es una escuela ejecutiva." },
        ]}, duration_min: 5 });
      toast.success("Programa completo creado con 2 módulos y 3 lecciones");
      onClose();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-4" data-testid="new-program-modal">
        <div className="flex items-start justify-between">
          <h3 className="text-xl font-semibold text-white">Nuevo programa · paso {step}/2</h3>
          <button onClick={onClose} className="text-gray-400"><X /></button>
        </div>
        {step === 1 && (
          <form onSubmit={createProgram} className="space-y-3">
            <Input label="Título" val={prog.title} on={(v) => setProg({ ...prog, title: v })} testid="np-title" req />
            <Input label="Código único (ej. DCC-901)" val={prog.code} on={(v) => setProg({ ...prog, code: v })} testid="np-code" req />
            <div>
              <label className="text-[11px] uppercase tracking-widest text-gray-400">Área</label>
              <select required value={prog.area_id} onChange={(e) => setProg({ ...prog, area_id: e.target.value })}
                data-testid="np-area"
                className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white">
                <option value="">Selecciona...</option>
                {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-gray-400">Formato</label>
                <select value={prog.format} onChange={(e) => setProg({ ...prog, format: e.target.value })}
                  className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white">
                  {["curso", "diplomado", "programa", "seminario", "certificacion"].map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-gray-400">Modalidad</label>
                <select value={prog.modality} onChange={(e) => setProg({ ...prog, modality: e.target.value })}
                  className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white">
                  {["online", "blended", "presencial", "mixto"].map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>
            <button type="submit" data-testid="np-next"
              className="w-full py-2.5 rounded-md bg-[#B84A0C] hover:bg-primary text-white font-semibold">
              Continuar →
            </button>
          </form>
        )}
        {step === 2 && (
          <div className="space-y-3">
            <p className="text-gray-300 text-sm">Se crearán automáticamente 2 módulos con 3 lecciones (texto, video y quiz) para que el programa esté listo para consumo.</p>
            <button onClick={finishFlow} data-testid="np-finish"
              className="w-full py-2.5 rounded-md bg-[#B84A0C] hover:bg-primary text-white font-semibold">
              Crear módulos y lecciones
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Input({ label, val, on, req, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-gray-400">{label}</label>
      <input required={req} value={val} onChange={(e) => on(e.target.value)} data-testid={testid}
        className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" />
    </div>
  );
}
